import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { usePortalName } from '../../../hooks/usePortalName';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import {
  selectEmailVerificationSuccess,
  setEmailVerificationSuccess,
  setLoggedInUserInfo,
} from '../../../data/user.slice';
import { setSuccessMessage } from '../../../data/ui.slice';

import VerifyAccountPageForm from '../../../components/member/auth/VerifyAccountPageForm';
import HomeSnackbar from '../../../components/home/HomeSnackbar';
import authService from '../../../services/authService';
import userService from '../../../services/userService';
import { usePortalClientBC } from '../../../hooks/usePortalClientBC';
import { useNavigate } from 'react-router-dom';

const PREFIX = 'MemberVerifyAccount';

const classes = {
  container: `${PREFIX}-container`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  center: `${PREFIX}-center`,
  logo: `${PREFIX}-logo`,
  formContainer: `${PREFIX}-formContainer`,
};

const portalName = usePortalName();
const heroImage = `${portalName}/auth_hero.webp`;
const logoSrc = `${portalName}/brand-logo-primary.svg`;

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100vh',
    backgroundSize: 'cover',
    backgroundImage: `url(${heroImage})`,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  [`& .${classes.left}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingLeft: theme.spacing(10),
    paddingTop: theme.spacing(30),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(2),
      justifyContent: 'center',
    },
  },
  [`& .${classes.right}`]: {
    flex: 1,
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'center',
    paddingRight: theme.spacing(10),
    paddingTop: theme.spacing(11),
    paddingBottom: theme.spacing(3),
    overflowY: 'auto',
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(2),
      justifyContent: 'center',
    },
  },
  [`& .${classes.center}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    height: '100vh',
    overflowY: 'auto',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
  },
  [`& .${classes.logo}`]: {
    width: 'auto',
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
    [theme.breakpoints.down('md')]: {
      maxWidth: 180,
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4),
    },
  },
  [`& .${classes.formContainer}`]: {
    position: 'relative',
    maxWidth: 375,
    width: '100%',
  },
}));

export default function MemberVerifyAccount() {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const portalName = usePortalName();
  const clientBC = usePortalClientBC(portalName || '');
  const federatedProvider = authService.getFederatedCustomProvider(portalName);

  const emailVerificationSuccess = useAppSelector(
    selectEmailVerificationSuccess,
  );

  useEffect(() => {
    if (emailVerificationSuccess) {
      dispatch(setSuccessMessage(t('member.emailVerified')));
      dispatch(setEmailVerificationSuccess(false));
    }
  }, [emailVerificationSuccess]);

  useEffect(() => {
    if (federatedProvider && clientBC) {
      authService.getTokens().subscribe((tokens) => {
        // This is a kludge for this release. We don't have a good way
        // of knowing if we are on a member or client portal until after
        // the user is logged in. Currently this code is only running
        // for AHC client and member portals so we can specifically check
        // the portal name to figure out which one it is.
        const isMemberPortal = portalName === 'ahc';
        userService
          .onboardFederatedUser(tokens.idToken!, clientBC, isMemberPortal)
          .subscribe({
            next: (userInfo) => {
              dispatch(setLoggedInUserInfo(userInfo));
            },
            error: (err) => {
              // eslint-disable-next-line no-console
              console.error('user failed to onboard', err);
            },
            complete: () => {
              navigate('/');
            },
          });
      });
    }
  }, [federatedProvider, clientBC]);

  if (federatedProvider && clientBC) {
    return null;
  }

  return (
    <Root className={classes.container}>
      <HomeSnackbar />
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        {isLarge && (
          <Box className={classes.left}>
            <img
              alt={`${t('member.logoAlt')}`}
              src={logoSrc}
              className={classes.logo}
            />
          </Box>
        )}
        {isLarge && (
          <Box className={classes.right}>
            <Box className={classes.formContainer}>
              <VerifyAccountPageForm />
            </Box>
          </Box>
        )}
        {!isLarge && (
          <Box className={classes.center}>
            <img
              alt={`${t('member.logoAlt')}`}
              src={logoSrc}
              className={classes.logo}
            />
            <Box className={classes.formContainer}>
              <VerifyAccountPageForm />
            </Box>
          </Box>
        )}
      </LocalizationProvider>
    </Root>
  );
}
